import {
  type DocumentData,
  type Firestore,
  arrayRemove,
  arrayUnion,
  collection,
  doc,
  getDoc,
  onSnapshot,
  serverTimestamp,
  setDoc,
  updateDoc,
} from 'firebase/firestore';
import {
  MESSAGE_TYPE_CONVERSATION,
  MessageSchema,
  MessageCreateInputSchema,
  MessageDeConversationInputSchema,
  MessageUpdateInputSchema,
  SansPersonne,
  CLES_DE_REACTION,
  cleDeReaction,
  fusionnerMentions,
  mentionsDe,
  type CleDeReaction,
  type Mentions,
  type Message,
  type MessageCreateInput,
  type MessageUpdateInput,
} from '@taki/data-model';
import { CURRENT_SCHEMA_VERSION, buildUpdateData, createConverter } from './standard-fields.js';
import {
  type CountOptions,
  type ListOptions,
  runAverage,
  runCount,
  runList,
  runSum,
} from './list-query.js';
import { createArchiveOps } from './archive-ops.js';
import { createObserveListOps, type ObserveListOps } from './observe-ops.js';
import { type MaPersonne, cadrageAcces } from './acces-par-fiche.js';

/**
 * Repository de la famille `message` (communication individuelle, org-wide en v0).
 *
 * Même patron que `asset` : l'entrée est VALIDÉE par Zod
 * (`MessageCreateInputSchema`) AVANT toute écriture Firestore. Les champs
 * standard sont remplis par le SDK, les requêtes `list` sont cadrées par orgId.
 * Les liens vers d'autres familles (fromRef, toRefs, threadRef, contact,
 * booking, invoice, ticket, campaign, review) sont de simples ids.
 *
 * Acces : la famille est HERITIERE d'un acces par fiche
 * (`docs/socle/acces-par-fiche.md`). Un message avec `conversationId` herite
 * des droits de sa conversation : le SDK lit la conversation a la creation et
 * recopie sa visibilite en REFLET (`acces: { visibilite }`), que les rules
 * verifient. Sans `conversationId`, le message est `equipe`. Les messages
 * d'une conversation se listent par `{ acces: { conversation: id } }`.
 */

/** La conversation d'un message qui nait est illisible : absente, supprimee, ou fermee a l'appelant. */
export class ConversationIllisible extends Error {
  constructor(public readonly conversationId: string) {
    super(
      `messages.create : la conversation ${conversationId} est introuvable ou fermee a ce compte. ` +
        "Un message n'entre que dans une conversation que son auteur peut lire.",
    );
    this.name = 'ConversationIllisible';
  }
}

/** Une reaction hors de la liste fermee (`REACTIONS_DE_MESSAGE`). */
export class ReactionInconnue extends Error {
  constructor(public readonly reaction: string) {
    super(`messages.reagir : la reaction ${JSON.stringify(reaction)} n'est pas dans la liste fermee (pouce, pouceBas, fete).`);
    this.name = 'ReactionInconnue';
  }
}

export type { MessageCreateInput };

/** Nom de la collection Firestore de la famille message. */
export const MESSAGES_COLLECTION = 'messages';

/**
 * Repository typé pour la famille `message`.
 * Toute lecture est validée par `MessageSchema`, toute écriture par
 * `MessageCreateInputSchema` (cage IA : aucun accès Firestore direct hors SDK).
 */
/** Les options d'`envoyer` : le fil, et ce que l'editeur sait des mentions (fusionne avec le texte). */
export interface OptionsDEnvoi {
  parentId?: string;
  mentions?: Partial<Mentions>;
}

/** Les options d'`observerConversation` : la fenetre (defaut 50, au plus 100). */
export interface OptionsDeConversation {
  limite?: number;
}

export const CONVERSATION_FENETRE_DEFAUT = 50;
export const CONVERSATION_FENETRE_MAX = 100;

/**
 * LE PORT D'UN FIL, tel que `@taki/ui` le recoit (`FilDeConversation`,
 * `ComposeurConversation`) : `sdk.messages` le satisfait tel quel.
 */
export interface PortDeFil {
  observerConversation(
    conversationId: string,
    options: OptionsDeConversation,
    /**
     * `meta.local` (V3-1 du 07/10/2026) : VRAI quand ce rendu contient un envoi
     * de ce client pas encore ecrit au serveur (envoi sans blocage). Un tel
     * message n'existe pas encore en base : on ne le relit pas, on n'acquitte
     * pas jusqu'a lui.
     */
    onChange: (messages: Message[], meta?: { local: boolean }) => void,
    onError?: (error: unknown) => void,
  ): () => void;
  observerFil(
    conversationId: string,
    parentId: string,
    onChange: (messages: Message[]) => void,
    onError?: (error: unknown) => void,
  ): () => void;
  envoyer(conversationId: string, texte: string, options?: OptionsDEnvoi): Promise<Message>;
  /** Remplace la reaction de l'appelant (une par personne et par message). `reaction` : la cle ou l'emoji. */
  reagir(messageId: string, reaction: string): Promise<void>;
  /** Retire la reaction de l'appelant, jamais celle d'un autre. */
  retirerReaction(messageId: string, reaction: string): Promise<void>;
}

export interface MessageRepository extends ObserveListOps<Message>, PortDeFil {
  // ---- Le contrat conversations du 05/10/2026 (docs/socle/conversations.md) ----
  /**
   * Un message de conversation : `type: 'conversation'`, `auteur` = le
   * personId de l'appelant, `mentions` derivees du texte. L'envoi ne marque
   * pas les autres messages lus ; l'hote acquitte les messages affiches.
   */
  envoyer(conversationId: string, texte: string, options?: OptionsDEnvoi): Promise<Message>;
  /** Les `limite` derniers messages (racines et reponses) de la conversation, du plus ancien au plus recent. */
  observerConversation(
    conversationId: string,
    options: OptionsDeConversation,
    onChange: (messages: Message[], meta?: { local: boolean }) => void,
    onError?: (error: unknown) => void,
  ): () => void;
  /** La racine et ses reponses, du plus ancien au plus recent. */
  observerFil(
    conversationId: string,
    parentId: string,
    onChange: (messages: Message[]) => void,
    onError?: (error: unknown) => void,
  ): () => void;

  create(input: MessageCreateInput): Promise<Message>;
  update(id: string, patch: MessageUpdateInput): Promise<Message>;
  list(options?: ListOptions): Promise<Message[]>;
  count(options?: CountOptions): Promise<number>;
  /** Somme serveur d'un champ, memes options que `count`. Cf. list-query.ts. */
  sum(field: string, options?: CountOptions): Promise<number>;
  /** Moyenne serveur d'un champ, `null` si aucun document. Cf. list-query.ts. */
  average(field: string, options?: CountOptions): Promise<number | null>;
  get(id: string): Promise<Message | null>;
  /** Archive (`archivedAt`). Semantique commune : cf. archive-ops.ts. */
  archive(id: string): Promise<Message>;
  /** Desarchive (`archivedAt` = null, JAMAIS deleteField). Cf. archive-ops.ts. */
  unarchive(id: string): Promise<Message>;
  /** Supprime (`deletedAt` + `archivedAt`), invisible cote client. Cf. archive-ops.ts. */
  remove(id: string): Promise<Message>;
}

// Relecture : le converter generique convertit deja TOUS les Timestamps
// (standard et metier) en Date, cf. standard-fields.ts.
const messageConverter = createConverter<Message>((raw: DocumentData) => MessageSchema.parse(raw));

/**
 * @param getActorId - retourne l'uid de l'utilisateur authentifié courant
 *   (injecté par `createTakiSdk`). Les Security Rules exigent
 *   createdBy/updatedBy == request.auth.uid.
 */
/**
 * @param marquerLu - marque une conversation lue pour l'appelant apres un
 *   envoi (`conversations-lecture.ts`) ; absent dans les tests.
 */
export function createMessageRepository(
  db: Firestore,
  getActorId: () => string,
  orgId: string,
  maPersonne: MaPersonne,
  marquerLu?: (conversationId: string) => Promise<void>,
  verifierEcriture?: (conversationId: string) => Promise<void>,
): MessageRepository {
  const col = collection(db, MESSAGES_COLLECTION).withConverter(messageConverter);
  const cadrage = (options?: ListOptions | CountOptions) => cadrageAcces('heritiere', options?.acces, maPersonne);

  /**
   * Le REFLET d'acces d'un message qui nait : la visibilite de sa conversation,
   * lue a l'instant (les rules relisent la meme conversation et refusent un
   * reflet qui ne lui correspond pas). Sans conversation : equipe.
   */
  async function refletDAcces(conversationId: string | undefined): Promise<{ visibilite: string }> {
    if (conversationId === undefined) return { visibilite: 'equipe' };
    let snap;
    try {
      snap = await getDoc(doc(db, 'conversations', conversationId));
    } catch (cause) {
      const code = (cause as { code?: unknown } | null)?.code;
      if (code === 'permission-denied') throw new ConversationIllisible(conversationId);
      throw cause;
    }
    if (!snap.exists() || snap.get('deletedAt') != null) throw new ConversationIllisible(conversationId);
    const visibilite = snap.get('acces.visibilite');
    return { visibilite: typeof visibilite === 'string' ? visibilite : 'equipe' };
  }
  // Archiver, desarchiver, supprimer : la semantique des trois etats vit dans
  // `createArchiveOps` (archive-ops.ts), ecrite une seule fois pour les 55
  // familles. Une famille n'a plus rien a decider ici.
  /**
   * Une seule reaction par personne : poser remplace les autres choix,
   * retirer les efface. Les transformations sont atomiques et ne touchent
   * pas aux reactions des autres personnes. Les rules verifient que seul
   * `maPersonne()` entre ou sort de la liste (docs/socle/conversations.md).
   */
  async function ecrireReaction(messageId: string, reaction: string, poser: boolean): Promise<void> {
    const cle: CleDeReaction | null = cleDeReaction(reaction);
    if (cle === null) throw new ReactionInconnue(reaction);
    const moi = await maPersonne();
    if (moi === null) throw new SansPersonne();
    await updateDoc(doc(db, MESSAGES_COLLECTION, messageId), {
      ...Object.fromEntries(CLES_DE_REACTION.map(autre => [
        `reactions.${autre}`, poser && autre === cle ? arrayUnion(moi) : arrayRemove(moi),
      ])),
      updatedAt: serverTimestamp(),
      updatedBy: getActorId(),
    });
  }

  const archiveOps = createArchiveOps<Message>(db, col, MESSAGES_COLLECTION, getActorId);
  const observeListOps = createObserveListOps<Message>(col, orgId, cadrage);

  return {
    ...observeListOps,

    async envoyer(conversationId, texte, options) {
      const entree = MessageDeConversationInputSchema.parse({
        conversationId,
        texte,
        ...(options?.parentId !== undefined ? { parentId: options.parentId } : {}),
        ...(options?.mentions !== undefined ? { mentions: options.mentions } : {}),
      });
      const auteur = await maPersonne();
      if (auteur === null) throw new SansPersonne();
      // Le chatter d'une fiche dont la base l'a eteint : refuse AVANT l'envoi (G16), par tout chemin (fiche, epingle, Canaux).
      if (verifierEcriture !== undefined) await verifierEcriture(entree.conversationId);
      const message = await this.create({
        orgId,
        type: MESSAGE_TYPE_CONVERSATION,
        conversationId: entree.conversationId,
        ...(entree.parentId !== undefined ? { parentId: entree.parentId } : {}),
        auteur,
        texte: entree.texte,
        mentions: fusionnerMentions(mentionsDe(entree.texte), entree.mentions),
      });
      return message;
    },

    async reagir(messageId, reaction) {
      await ecrireReaction(messageId, reaction, true);
    },

    async retirerReaction(messageId, reaction) {
      await ecrireReaction(messageId, reaction, false);
    },

    observerConversation(conversationId, options, onChange, onError) {
      const limite = Math.min(Math.max(1, Math.trunc(options.limite ?? CONVERSATION_FENETRE_DEFAUT)), CONVERSATION_FENETRE_MAX);
      return observeListOps.observeList(
        { type: MESSAGE_TYPE_CONVERSATION, acces: { conversation: conversationId }, orderBy: { field: 'createdAt', dir: 'desc' }, limit: limite },
        (messages, meta) => onChange([...messages].reverse(), meta),
        onError,
      );
    },

    observerFil(conversationId, parentId, onChange, onError) {
      let racine: Message | null = null;
      let reponses: Message[] = [];
      const publier = () => onChange(racine === null ? reponses : [racine, ...reponses]);
      const arrets = [
        onSnapshot(doc(col, parentId), (snap) => { racine = snap.exists() ? snap.data() : null; publier(); }, onError),
        observeListOps.observeList(
          { type: MESSAGE_TYPE_CONVERSATION, acces: { conversation: conversationId }, where: { parentId }, orderBy: { field: 'createdAt', dir: 'asc' }, limit: CONVERSATION_FENETRE_MAX },
          (messages) => { reponses = messages; publier(); },
          onError,
        ),
      ];
      return () => { for (const arret of arrets) arret(); };
    },

    async create(input: MessageCreateInput): Promise<Message> {
      // Valider AVANT d'écrire : une entrée invalide échoue ici, rien n'atteint
      // Firestore. (Même doctrine que le patron asset.)
      const fields = MessageCreateInputSchema.parse(input);

      const actor = getActorId();
      const ref = doc(col);
      const now = new Date();

      // Champs métier optionnels : on ne les écrit que s'ils sont fournis
      // (évite d'ancrer des `undefined` parasites dans Firestore).
      const optional: DocumentData = {};
      if (fields.channel !== undefined) optional['channel'] = fields.channel;
      if (fields.direction !== undefined) optional['direction'] = fields.direction;
      if (fields.fromRef !== undefined) optional['fromRef'] = fields.fromRef;
      if (fields.toRefs !== undefined) optional['toRefs'] = fields.toRefs;
      if (fields.subject !== undefined) optional['subject'] = fields.subject;
      if (fields.body !== undefined) optional['body'] = fields.body;
      if (fields.sentAt !== undefined) optional['sentAt'] = fields.sentAt;
      if (fields.deliveryStatus !== undefined) optional['deliveryStatus'] = fields.deliveryStatus;
      if (fields.threadRef !== undefined) optional['threadRef'] = fields.threadRef;
      if (fields.conversationId !== undefined) optional['conversationId'] = fields.conversationId;
      if (fields.contactId !== undefined) optional['contactId'] = fields.contactId;
      if (fields.bookingId !== undefined) optional['bookingId'] = fields.bookingId;
      if (fields.invoiceId !== undefined) optional['invoiceId'] = fields.invoiceId;
      if (fields.ticketId !== undefined) optional['ticketId'] = fields.ticketId;
      if (fields.campaignId !== undefined) optional['campaignId'] = fields.campaignId;
      if (fields.reviewId !== undefined) optional['reviewId'] = fields.reviewId;
      if (fields.customFields !== undefined) optional['customFields'] = fields.customFields;
      if (fields.couleur != null) optional['couleur'] = fields.couleur;
      if (fields.photo != null) optional['photo'] = fields.photo;
      if (fields.parentId !== undefined) optional['parentId'] = fields.parentId;
      if (fields.auteur !== undefined) optional['auteur'] = fields.auteur;
      if (fields.texte !== undefined) optional['texte'] = fields.texte;
      if (fields.mentions !== undefined) optional['mentions'] = fields.mentions;

      const payload: DocumentData = {
        orgId: fields.orgId,
        type: fields.type,
        ...optional,
        acces: await refletDAcces(fields.conversationId),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: actor,
        updatedBy: actor,
        archivedAt: null,
        deletedAt: null,
        schemaVersion: CURRENT_SCHEMA_VERSION,
      };

      await setDoc(ref, payload as unknown as Message);

      // setDoc ne résout pas serverTimestamp() : on valide une projection
      // cohérente avec ce qui vient d'être écrit (sentAt est déjà une Date issue
      // de l'entrée validée).
      return MessageSchema.parse({
        id: ref.id,
        ...payload,
        createdAt: now,
        updatedAt: now,
      });
    },

    async update(id: string, patch: MessageUpdateInput): Promise<Message> {
      // Valider AVANT d'écrire : même doctrine que create.
      const fields = MessageUpdateInputSchema.parse(patch);
      const actor = getActorId();

      // Seules les clés réellement fournies sont écrites (patch partiel, merge).
      // Patch transforme pour updateDoc : customFields est eclate en chemins
      // pointes ('customFields.<cle>') pour fusionner cle par cle au lieu de
      // remplacer la map entiere (cf. buildUpdateData ; null = deleteField).
      const data = buildUpdateData(fields);
      data['updatedAt'] = serverTimestamp();
      data['updatedBy'] = actor;

      // Ref brute (sans converter) pour l'écriture partielle : une ref convertie
      // exigerait le document complet.
      await updateDoc(doc(db, MESSAGES_COLLECTION, id), data);

      // Relire via la ref convertie : le document réel est la source de vérité,
      // et le converter le valide avec MessageSchema.
      const snap = await getDoc(doc(col, id));
      if (!snap.exists()) {
        throw new Error(`message ${id} introuvable après mise à jour.`);
      }
      return snap.data();
    },

    async list(options?: ListOptions): Promise<Message[]> {
      // Cadrage orgId, exclusion des archives, sous-type, tri, taille de page,
      // curseur et ACCES (equipe par defaut, ou une conversation) : toute la
      // semantique vit dans `runList` (list-query.ts), ecrite une seule fois.
      return runList(col, orgId, options, await cadrage(options));
    },

    async count(options?: CountOptions): Promise<number> {
      // Compter sans charger : meme cadrage que `list`, sans page ni tri
      // (cf. runCount, list-query.ts). Un compteur calcule sur la page
      // courante ment des que la liste est paginee.
      return runCount(col, orgId, options, await cadrage(options));
    },

    async sum(field: string, options?: CountOptions): Promise<number> {
      // Sommer sans charger, meme cadrage que `count` (cf. runSum,
      // list-query.ts). Rend 0 quand aucun document ne passe le filtre.
      return runSum(col, orgId, field, options, await cadrage(options));
    },

    async average(field: string, options?: CountOptions): Promise<number | null> {
      // `null` quand aucun document ne passe le filtre : c'est ce que rend
      // Firestore, et un zero mentirait (cf. runAverage, list-query.ts).
      return runAverage(col, orgId, field, options, await cadrage(options));
    },

    async get(id: string): Promise<Message | null> {
      const ref = doc(col, id);
      const snap = await getDoc(ref);
      if (!snap.exists()) return null;
      return snap.data();
    },

    // archive / unarchive / remove : cf. `archiveOps` plus haut.
    ...archiveOps,
  };
}
